import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { monotonicNow, useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';
import { useScreenRequest } from '../hooks/useScreenRequest';
import { DESKTOP, useMediaQuery } from '../hooks/useMediaQuery';
import { useAged } from '../hooks/useScreenData';
import { EmptyState, InlineError, LoadingState, StaleNotice } from '../components/PageStates';
import { actorText, formatUtc, invalidFilter, orNotRecorded, targetText, utcInstant } from './auditFields';
import './Audit.css';

// Exactly the filters server/schemas/platform-audit.schemas.js accepts (#583 S4); nothing else is sent.
const CATEGORIES = [['all', 'All'], ['auth', 'Operator sign-in'], ['tenants', 'Tenants'], ['data', 'Member data']];
const TEXT_FILTERS = [
  ['q', 'Search', 'search'], ['adminId', 'Actor ID', 'text'], ['tenantId', 'Tenant ID', 'text'],
  ['action', 'Action', 'text'], ['from', 'From (UTC)', 'datetime-local'], ['to', 'To (UTC)', 'datetime-local'],
];
const NO_FILTERS = { category: 'all', q: '', adminId: '', tenantId: '', action: '', from: '', to: '' };

function queryOf(filters, cursor) {
  const params = { category: filters.category };
  for (const [key] of TEXT_FILTERS) {
    const value = filters[key].trim();
    if (value) params[key] = key === 'from' || key === 'to' ? utcInstant(value) : value;
  }
  if (cursor) params.cursor = cursor;
  return params;
}

const labelOf = (key) => TEXT_FILTERS.find(([name]) => name === key)[1];

/** The list state kept in browser history, re-checked: anything could have written it. */
export function restore(saved) {
  if (!saved || typeof saved !== 'object') return null;
  const filters = { ...NO_FILTERS };
  for (const key of Object.keys(NO_FILTERS)) {
    if (typeof saved.filters?.[key] === 'string') filters[key] = saved.filters[key];
  }
  // A refused value is dropped, and with it the cursors bound to the filters it belonged to.
  let changed = !CATEGORIES.some(([value]) => value === filters.category);
  if (changed) filters.category = 'all';
  for (let bad = invalidFilter(filters); bad; bad = invalidFilter(filters)) {
    filters[bad[0]] = '';
    changed = true;
  }
  const stack = !changed && Array.isArray(saved.stack) && saved.stack.every((c) => typeof c === 'string' && c.length > 0 && c.length <= 2048 && /^[A-Za-z0-9_-]+$/.test(c))
    ? saved.stack : [];
  return { filters, stack };
}

export default function AuditLog() {
  const location = useLocation();
  const navigate = useNavigate();
  const { rememberAuditList, recallAuditList } = useAuth();
  const [initial] = useState(() => restore(recallAuditList(location.state?.auditList)) || { filters: NO_FILTERS, stack: [] });
  const [draft, setDraft] = useState(initial.filters);
  const [view, setView] = useState({ ...initial, reload: 0 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableOnNarrow, setTableOnNarrow] = useState(false);
  const [invalid, setInvalid] = useState(null);
  const moreFilters = useRef(null);
  const wide = useMediaQuery(DESKTOP);
  const ids = useId();

  const cursor = view.stack.at(-1);
  const key = JSON.stringify([view.filters, view.stack]);
  const { read } = useScreenRequest(key);

  useEffect(() => {
    setLoading(true);
    setInvalid(null);
    setError(null);
    read((options) => api.listAudit(queryOf(view.filters, cursor), options), {
      success: (data) => setResult({ key, data, loadedAt: new Date(), at: monotonicNow() }),
      error: setError,
      settled: () => setLoading(false),
    });
  }, [read, view, key, cursor]);

  const historyHandle = useMemo(() => rememberAuditList({ filters: view.filters, stack: view.stack }),
    [rememberAuditList, view.filters, view.stack]);

  // This history entry references the applied query and page, so the browser's own Back/Forward
  // restore them as the detail page's Back link does (§7.3).
  useEffect(() => {
    navigate(location.pathname, { replace: true, state: { auditList: historyHandle } });
  }, [navigate, location.pathname, historyHandle]);

  // Only an answer to THIS query is shown; a failed reload of it keeps it, labelled stale.
  const badCursor = error?.data?.type === 'INVALID_CURSOR';
  const current = !badCursor && result?.key === key ? result.data : null;
  const stale = Boolean(error && current?.items.length);
  // §7.5: a page not reloaded successfully for 60 s is stale too. Either way paging waits for a reload.
  const aged = useAged(current ? result.at : null);
  const staleReason = loading ? null : stale ? 'failed' : aged && !error ? 'aged' : null;
  const linkState = { auditList: historyHandle };

  const reload = (change = {}) => setView((v) => ({ ...v, ...change, reload: v.reload + 1 }));
  const apply = (e) => {
    e.preventDefault();
    const bad = invalidFilter(draft);
    setInvalid(bad && `${labelOf(bad[0])} must be ${bad[1]}.`);
    if (bad && moreFilters.current) moreFilters.current.open = true;
    if (!bad) reload({ filters: draft, stack: [] });
  };
  const setField = (name) => (e) => {
    setInvalid(null);
    setDraft((d) => ({ ...d, [name]: e.target.value }));
  };

  const columns = [
    ['Entry', (e) => <Link to={`/audit/${e.id}`} state={linkState}>Entry {e.id}</Link>],
    ['Time', (e) => <time dateTime={e.createdAt}>{formatUtc(e.createdAt)}</time>],
    ['Action', (e) => e.action],
    ['Category', (e) => e.category],
    ['Actor', (e) => actorText(e.actor)],
    ['Target', (e) => targetText(e.target)],
    ['Tenant', (e) => orNotRecorded(e.tenantId)],
  ];
  const asTable = wide || tableOnNarrow;
  const textFields = TEXT_FILTERS.map(([name, label, type]) => (
    <div className="field" key={name}>
      <label htmlFor={`${ids}${name}`}>{label}</label>
      <input id={`${ids}${name}`} type={type} value={draft[name]} onChange={setField(name)} />
    </div>
  ));

  return (
    <div className="audit-page">
      <div className="page-head">
        <h2>Audit</h2>
        <button type="button" className="btn btn-outline" onClick={() => reload({ stack: [] })}>Refresh</button>
      </div>

      <form className="audit-filters" aria-label="Audit filters" onSubmit={apply}>
        <div className="field">
          <label htmlFor={`${ids}category`}>Category</label>
          <select id={`${ids}category`} value={draft.category} onChange={setField('category')}>
            {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        {wide ? textFields : (
          <details className="audit-more-filters" ref={moreFilters} open={TEXT_FILTERS.some(([name]) => initial.filters[name])}>
            <summary>More filters</summary>
            {textFields}
          </details>
        )}
        <button type="submit" className="btn btn-primary">Apply filters</button>
      </form>

      {invalid && <InlineError title="Check the filters" message={invalid} />}
      {loading && <LoadingState label="Loading audit entries" lines={6} />}
      {!loading && !invalid && badCursor && (
        <InlineError title="This page is no longer valid" message="The filters changed or the page expired." actionLabel="Reset page" onAction={() => reload({ stack: [] })} />
      )}
      {!loading && !invalid && error && !badCursor && !stale && (error.status === 400
        ? <InlineError title="Check the filters" message={error.message} />
        : <InlineError message={error.message} onAction={() => reload()} />)}
      {staleReason && <StaleNotice loadedAt={result.loadedAt} reason={staleReason} onReload={() => reload()} />}

      {!loading && !error && current && current.items.length === 0 && (
        <EmptyState title="No entries match">No audit entry matches these filters. Without a time range, the last 30 days are searched.</EmptyState>
      )}

      {!loading && current && current.items.length > 0 && (
        <>
          {!wide && (
            <button type="button" className="btn btn-outline" onClick={() => setTableOnNarrow((t) => !t)}>
              {tableOnNarrow ? 'View cards' : 'View table'}
            </button>
          )}
          {asTable ? (
            <div className="table-region" role="region" aria-label="Audit table" tabIndex={0}>
              <table className="data-table">
                <caption>Audit entries, newest first</caption>
                <thead><tr>{columns.map(([label]) => <th key={label} scope="col">{label}</th>)}</tr></thead>
                <tbody>
                  {current.items.map((e) => (
                    <tr key={e.id}>{columns.map(([label, cell]) => <td key={label}>{cell(e)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ul className="audit-cards" role="list" aria-label="Audit entries">
              {current.items.map((e) => (
                <li key={e.id} className="card">
                  <dl>{columns.map(([label, cell]) => <div key={label}><dt>{label}</dt><dd>{cell(e)}</dd></div>)}</dl>
                </li>
              ))}
            </ul>
          )}
          <p className="as-of">Entries up to {formatUtc(current.asOf)}</p>
        </>
      )}

      <div className="pagination">
        <span>Page {view.stack.length + 1}</span>
        <div className="pagination-buttons">
          <button type="button" className="btn btn-outline" disabled={loading || Boolean(error) || Boolean(staleReason) || view.stack.length === 0}
            onClick={() => setView((v) => ({ ...v, stack: v.stack.slice(0, -1) }))}>Previous page</button>
          <button type="button" className="btn btn-outline" disabled={loading || Boolean(error) || Boolean(staleReason) || !current?.nextCursor}
            onClick={() => setView((v) => ({ ...v, stack: [...v.stack, current.nextCursor] }))}>Next page</button>
        </div>
      </div>
    </div>
  );
}
